export const NODESHIFT_AI = "NodeShift AI!";

export const DATE_FORMAT = "MMM d, yyyy";

export const DATE_TIME_FORMAT = "MMM d, yyyy hh:mm a";

export const DATE_TIME_FORMAT_WITH_SEC = "MMM d, yyyy hh:mm:ss a";

// NOTE: NSAI-4728 — the time half of a split date/time cell. Mobile's flagged
// list stacks the date above the time in a narrow column, where web prints one
// combined string, so mobile needs the time on its own. Kept in step with
// DATE_TIME_FORMAT above (12-hour + meridiem for en, 24-hour for ru) so a
// stacked cell and a combined one cannot disagree.
export const TIME_FORMAT = "hh:mm a";

export const TIME_FORMAT_RU = "HH:mm";

export const SHORT_DATE_FORMAT = "MMM d";

export const SHORT_DATE_FORMAT_RU = "dd.MM";

export const DATE_FORMAT_RU = "dd.MM.yyyy";

export const DATE_TIME_FORMAT_RU = "dd.MM.yyyy HH:mm";

export const DATE_TIME_FORMAT_WITH_SEC_RU = "dd.MM.yyyy HH:mm:ss";

// NOTE: NSAI-5022 — the chat message timestamp is formatted by `Intl`, not a
// date-fns pattern, so each language gets its own numeric order and separators
// (`08/14/2026, 08:46 PM`, `14.08.2026, 20:46`, `14/08/2026، 08:46 PM`) without
// a hand-written `_RU` / `_AR` twin per language. The hour cycle is left to the
// locale: 12-hour for en and ar, 24-hour for ru.
export const MESSAGE_TIMESTAMP_FORMAT_OPTIONS = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
} as const satisfies Intl.DateTimeFormatOptions;

// NOTE: NSAI-4801 — the meeting and upload rows lead with the weekday, which is
// worth keeping: those lists are read to find "the Thursday call". Unlike the
// patterns above there is no `_RU` twin, because this one is already day-first
// and so reads correctly in all three languages once the locale is supplied —
// the day and month names come from the locale, not from the pattern. Web and
// mobile both render it, hence shared: the identical hardcoded literal sat in
// each app's own `UploadedMeetingRow` and printed English in an Arabic UI.
export const WEEKDAY_DATE_FORMAT = "EEEE, d MMM, yyyy";

export const MIN_PASSWORD_LENGTH = 8;

export const MAX_PASSWORD_LENGTH = 128;

export const PASSWORD_PATTERN =
  /^(?=.*\p{Ll})(?=.*\p{Lu})(?=.*\d)(?=.*[^\p{L}\d]).+$/u;

// NOTE: Regex to ensure at least one Unicode letter or digit
export const HAS_ALPHANUMERIC_REGEX = /[\p{L}\p{N}]/u;

// NOTE: Domain validation regex pattern
// Matches valid domain names (e.g., example.com, subdomain.example.com)
// Does not match localhost or IP addresses
export const DOMAIN_PATTERN =
  /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

export const SELECT_CREATE_OPTION_VALUE = "__create_new__";

export const DEFAULT_CACHE_STALE_TIME_MS = 5 * 60 * 1000; // Cache for 5 minutes

export const HTTP_STATUS_UNAUTHORIZED = 401;
export const HTTP_STATUS_FORBIDDEN = 403;
export const HTTP_STATUS_NOT_FOUND = 404;
export const HTTP_STATUS_CONFLICT = 409;
export const HTTP_STATUS_GONE = 410;
export const HTTP_STATUS_TOO_MANY_REQUESTS = 429;
export const HTTP_STATUS_INTERNAL_SERVER_ERROR = 500;

// NOTE: A threshold rather than a status: everything at or above it is a
// server-side failure the same request may safely be retried against. Shares
// the value of HTTP_STATUS_INTERNAL_SERVER_ERROR but not its meaning — that one
// names one status, this one opens a range — so the retry rule reads as a
// comparison against a floor instead of against a specific code.
export const HTTP_STATUS_SERVER_ERROR_FLOOR = 500;

// NOTE: NSAI-4624 — backoff bounds for a retryable request. The base is well
// under the 2s the backpressure gate asks for (SHED_RETRY_AFTER_SECONDS), so a
// server that names its own delay always wins; the cap keeps an exponential
// sequence, or an implausible `retryAfterSeconds`, from parking a spinner on
// screen for minutes.
export const MS_PER_SECOND = 1000;
export const HTTP_RETRY_BASE_DELAY_MS = 500;
export const HTTP_RETRY_MAX_DELAY_MS = 8000;

// NOTE: NSAI-4887 — the exact body message the server answers with when the
// failure was NOT an HttpException, i.e. an unhandled server-side error
// (`GENERIC_INTERNAL_ERROR_MESSAGE` in
// `apps/nsai/server/src/utils/all-exceptions.filter.ts`). It is a fixed English
// sentinel, not a localized explanation, so rendering it tells the user
// nothing they can act on: a corrupt PDF dropped into the probe uploader came
// back as a card reading "Internal server error". Clients match on it to fall
// through to their own wording. Keep the two strings in step.
export const GENERIC_SERVER_ERROR_MESSAGE = "Internal server error";

export const SEARCHABLE_LIST_MIN_ITEMS_FOR_SEARCH = 6 as const;

// NOTE: Normalized model score display range (0-100%).
// NOTE: Raw DB scores can freely exceed this range (e.g. a new top-performing model
// NOTE: may be added with quality: 120). The normalization utility divides each raw
// NOTE: score by the current maximum, so the best model always displays as 100% and
// NOTE: all others scale proportionally. These constants define the Add Model form's
// NOTE: default input boundaries only.
export const MIN_NORMALIZED_MODEL_SCORE = 0;
export const MAX_NORMALIZED_MODEL_SCORE = 100;

// NOTE: Regex to validate a model score string in the 0-100 range (Add Model form)
export const MODEL_SCORE_REGEX = /^(100|[1-9]?\d)$/;

// NOTE: DB column limits for model string and integer fields
export const MAX_MODEL_FIELD_LENGTH = 255;
export const MAX_MODEL_INTEGER_VALUE = 2147483647;

// NOTE: NSAI-4494 — taglines render inline in the model dropdowns, so they are
// capped much tighter than regular model string fields. Mirrors
// MAX_MODEL_TAGLINE_LENGTH in the server's models.utils.
export const MAX_MODEL_TAGLINE_LENGTH = 64;

// NOTE: Saved as `maxMessagesPerUser` when a custom model is created with
// "unlimited messages per user" on. The server treats any cap at or above this as
// unlimited. Mirrors UNLIMITED_MAX_MESSAGES_PER_USER in the server's constants.
export const UNLIMITED_MAX_MESSAGES_PER_USER = 2_000_000_000;

// NOTE: HTTP path of the access-token refresh endpoint. Currently only
// the web notifications-websocket lib consumes it directly — that file
// talks to the API via the raw `axios` import (not the project's
// ApiClient instance) because the ApiClient's interceptor would loop
// through this same refresh path on failure. Mobile resolves the refresh
// via the typed `authApi.refreshToken()` service and doesn't reference
// this constant. Kept in frontend-shared so any future raw-axios consumer
// (web or mobile) can pick up the same path in one place.
export const AUTH_REFRESH_ENDPOINT = "/api/auth/refresh";

// NOTE: NSAI-6810 — ceiling on one access-token refresh by the ApiClient.
// Concurrent 401s share a single refresh there, so a refresh that never
// answers would hold every one of them, and an HTTP stack without a timeout of
// its own can wait indefinitely (React Native's Android stack sets none by
// default). A timed-out refresh fails like a dropped connection: the session
// stays and the next 401 starts a new refresh.
export const AUTH_REFRESH_TIMEOUT_MS = 15_000;

// NOTE: The scheme prefix of the Authorization header every authenticated
// request carries.
export const AUTH_BEARER_PREFIX = "Bearer ";
